import axios from 'axios'
import type { AdminAuthSession, AdminCredentials, LoginResult } from '@/api/login/types'
import { useUserStoreWithOut } from '@/store/modules/user'
import { setOssSession } from '@/utils/ossImageAccess'

export const ADMIN_SESSION_KEY = 'admin_auth_session_v1'
let loggingOut = false

export class AdminSessionExpiredError extends Error {}

const validCredentials = (value: unknown): value is AdminCredentials => {
  if (!value || typeof value !== 'object') return false
  const data = value as AdminCredentials
  return (
    typeof data.token === 'string' &&
    data.token.trim() !== '' &&
    Number.isInteger(data.expire) &&
    data.expire > 0 &&
    typeof data.sessionId === 'string' &&
    /^[a-f0-9]{32}$/.test(data.sessionId) &&
    typeof data.refreshToken === 'string' &&
    /^[a-f0-9]{64}$/.test(data.refreshToken) &&
    Number.isInteger(data.refreshExpire) &&
    data.refreshExpire > 0
  )
}

export const readAdminSession = (): AdminAuthSession | null => {
  const raw = localStorage.getItem(ADMIN_SESSION_KEY)
  if (!raw) return null
  let value: AdminAuthSession
  try {
    value = JSON.parse(raw)
  } catch {
    return null
  }
  if (
    !validCredentials(value) ||
    !value.user ||
    typeof value.user.adminId !== 'string' ||
    typeof value.pendingRefreshToken !== 'string' ||
    (value.pendingRefreshToken !== '' && !/^[a-f0-9]{64}$/.test(value.pendingRefreshToken))
  )
    return null
  return value
}

const syncSession = () => {
  const userStore = useUserStoreWithOut()
  if (userStore.reloadRequired) throw sessionChanged()
  const session = readAdminSession()
  if (userStore.getSessionIdentity && userStore.getSessionIdentity !== (session?.sessionId || '')) {
    // storage 事件可能晚于 focus / 业务请求；旧页面绝不能静默使用新账号身份。
    userStore.reloadRequired = true
    userStore.session = null
    userStore.setRoleRouters([])
    setOssSession('')
    window.location.reload()
    throw sessionChanged()
  }
  userStore.session = session
  return session
}

const withAuthLock = <T>(action: () => Promise<T>): Promise<T> => {
  // 使用浏览器原生锁，让同一来源的标签页串行轮换同一组刷新凭据。
  if (!navigator.locks)
    return Promise.reject(
      new Error('当前浏览器不支持安全登录续期，请使用新版浏览器并通过 HTTPS 访问')
    )
  return navigator.locks.request('eat-tea-admin-auth', action)
}

const sessionChanged = () => new Error('登录状态已变更，请重试')
const assertCurrent = (sessionId: string) => {
  if (readAdminSession()?.sessionId !== sessionId) throw sessionChanged()
}

const postCredentials = async (path: string, session: AdminAuthSession) => {
  // 独立请求不经过业务拦截器，避免刷新递归与过期 Bearer 干扰。
  const response = await axios.post(
    path,
    {
      sessionId: session.sessionId,
      refreshToken: session.refreshToken,
      ...(session.pendingRefreshToken ? { nextRefreshToken: session.pendingRefreshToken } : {})
    },
    { baseURL: import.meta.env.VITE_API_BASE_PATH, timeout: 15000 }
  )
  const body = response.data
  if (!body || !Number.isInteger(body.code)) throw new Error('登录服务响应格式异常')
  if (body.code === 401) throw new AdminSessionExpiredError(body.msg || '登录已过期，请重新登录')
  if (body.code !== 0) throw new Error(body.msg || '登录服务暂时不可用，请稍后重试')
  return body.data as unknown
}

export const acceptAdminLogin = (data: LoginResult) =>
  withAuthLock(async () => {
    if (!validCredentials(data) || !data.user || typeof data.user.adminId !== 'string')
      throw new Error('登录会话响应格式异常')
    useUserStoreWithOut().setSession({ ...data, pendingRefreshToken: '' })
  })

export const ensureAdminSession = async (rejectedToken?: string) => {
  if (loggingOut) throw sessionChanged()
  const initial = syncSession()
  if (!initial) return
  const now = Math.floor(Date.now() / 1000)
  if (
    !initial.pendingRefreshToken &&
    initial.expire > now + 60 &&
    (!rejectedToken || rejectedToken !== initial.token)
  )
    return
  await withAuthLock(async () => {
    assertCurrent(initial.sessionId)
    if (loggingOut) throw sessionChanged()
    let session = syncSession()!
    const currentNow = Math.floor(Date.now() / 1000)
    if (
      !session.pendingRefreshToken &&
      session.expire > currentNow + 60 &&
      (!rejectedToken || rejectedToken !== session.token)
    )
      return
    const userStore = useUserStoreWithOut()
    if (!session.pendingRefreshToken) {
      const bytes = crypto.getRandomValues(new Uint8Array(32))
      const nextRefreshToken = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
        ''
      )
      session = { ...session, pendingRefreshToken: nextRefreshToken }
      // 同步写盘成功后才发送，丢响应或关闭标签页后仍可恢复同一轮换。
      userStore.setSession(session)
    }
    let data: unknown
    try {
      data = await postCredentials('/adminApi/auth/refresh', session)
    } catch (error) {
      assertCurrent(session.sessionId)
      if (error instanceof AdminSessionExpiredError) userStore.logout()
      throw error
    }
    assertCurrent(session.sessionId)
    if (
      !validCredentials(data) ||
      data.sessionId !== session.sessionId ||
      data.refreshToken !== session.pendingRefreshToken
    )
      throw new Error('续期响应格式异常')
    userStore.setSession({ ...session, ...data, pendingRefreshToken: '' })
  })
}

export const logoutAdminSession = async () => {
  if (loggingOut) throw sessionChanged()
  const initial = syncSession()
  loggingOut = true
  try {
    await withAuthLock(async () => {
      const session = syncSession()
      if (!session) {
        useUserStoreWithOut().logout()
        return
      }
      if (session.sessionId !== initial?.sessionId) throw sessionChanged()
      try {
        const data = await postCredentials('/adminApi/auth/logout', session)
        if (!data || typeof data !== 'object' || (data as { success?: unknown }).success !== true) {
          throw new Error('退出响应格式异常')
        }
      } catch (error) {
        if (!(error instanceof AdminSessionExpiredError)) throw error
      }
      assertCurrent(session.sessionId)
      useUserStoreWithOut().logout()
    })
  } finally {
    loggingOut = false
  }
}

export const startAdminSession = () => {
  const restore = () => {
    void ensureAdminSession().catch(() => {})
  }
  window.addEventListener('storage', (event) => {
    if (event.key !== ADMIN_SESSION_KEY && event.key !== null) return
    try {
      syncSession()
    } catch {
      /* 身份切换已启动页面刷新，并阻断旧页面请求。 */
    }
  })
  window.addEventListener('focus', restore)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) restore()
  })
  restore()
}
